<template>
  <div class="the-app-bar__notify">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      offset-y
      internal-activator
      :left="true"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="attrs" v-on="on" text plain>
          <v-icon color="n2" :class="{ Y1: menu }">mdi-bell-outline</v-icon>
          <span
            class="the-app-bar__notify-badge"
            v-if="has_unread_notifications"
          />
        </v-btn>
      </template>

      <v-card class="Notifications__card" min-width="384">
        <div class="Notifications__card_header">
          <span class="header_text"> Notifications </span>
          <v-btn 
            icon 
            small
            @click="goToSettings"
            class="settings_icon"
            title="Notification Settings"
          >
            <v-icon small>mdi-cog-outline</v-icon>
          </v-btn>
        </div>
        
        <!-- Tab Navigation -->
        <div class="Notifications__tabs">
          <button 
            class="Notifications__tab"
            :class="{ active: activeTab === 'activity' }"
            @click="setActiveTab('activity')"
          >
            Activity
            <span 
              v-if="unreadCounts.activity > 0" 
              class="Notifications__tab-badge"
            >
              {{ unreadCounts.activity }}
            </span>
          </button>
          <button 
            class="Notifications__tab"
            :class="{ active: activeTab === 'global' }"
            @click="setActiveTab('global')"
          >
            News & Updates
            <span 
              v-if="unreadCounts.global > 0" 
              class="Notifications__tab-badge"
            >
              {{ unreadCounts.global }}
            </span>
          </button>
        </div>
        <!-- Activity Notifications -->
        <v-list
          class="Notifications__list"
          id="Notifications__list"
          v-if="activeTab === 'activity' && activityNotifications.length > 0"
        >
          <v-list-item
            v-for="notification in activityNotifications"
            :key="notification.id"
            @click="moveToObject(notification)"
          >
            <div class="d-inline-block Notifications__avatar_image">
              <img
                v-if="!avatarEmpty(notification)"
                :src="notification.createdAvatarUrl"
                alt=""
                class="the-app-bar__image-wrapper"
              />
              <avatar
                v-else
                :rounded="true"
                :initials="initials(notification)"
                username="name"
                :size="26"
              />
            </div>
            <div class="d-inline-block Notifications__text">
              <p v-html="notification.header"></p>
              <div class="Notifications__timestamps">
                {{ notification.createdAt }}
                <span
                  class="Notifications__version_number"
                  v-if="
                    ['comments', 'mentions'].includes(
                      notification.notificationType
                    )
                  "
                >
                  <div class="version_dot"></div>
                  <span>
                    Version #{{ notification.linkInfo['scoped_id'] }}
                  </span>
                </span>
              </div>

              <div
                v-if="
                  ['comments', 'mentions'].includes(
                    notification.notificationType
                  )
                "
                class="Notifications__html_message"
              >
                <div v-html="notification.htmlText"></div>
              </div>
            </div>
            <div
              class="Notifications__unread_dot"
              v-if="notification.viewed == false"
            />
            <div
              class="viewed_button"
              @click.stop="changeViewedState(notification)"
            >
              <v-icon v-if="notification.viewed == false"
                >mdi-checkbox-marked-circle-outline
              </v-icon>
              <v-icon v-else>mdi-check-circle</v-icon>
            </div>
          </v-list-item>
        </v-list>

        <!-- Global Notifications -->
        <div
          class="Notifications__list Notifications__global-list"
          v-if="activeTab === 'global' && globalNotifications.length > 0"
        >
          <GlobalNotificationCard
            v-for="notification in globalNotifications"
            :key="notification.id"
            :notification="notification"
            @mark-as-read="markGlobalNotificationAsRead"
          />
        </div>

        <!-- Empty States -->
        <div 
          v-if="activeTab === 'activity' && activityNotifications.length === 0"
          class="Notifications__empty-state"
        >
          <v-icon color="n2" large>mdi-bell-outline</v-icon>
          <p>No activity notifications</p>
        </div>

        <div 
          v-if="activeTab === 'global' && globalNotifications.length === 0"
          class="Notifications__empty-state"
        >
          <v-icon color="n2" large>mdi-newspaper-variant-outline</v-icon>
          <p>No news or updates at the moment</p>
          <small>Check back later for product updates and announcements</small>
        </div>
      </v-card>
      <div class="Notifications__footer">
        <v-btn 
          text 
          color="#ff7e21" 
          @click.stop="markAllAsRead"
          v-if="hasUnreadNotifications"
        >
          Mark all as read
        </v-btn>
        <v-btn 
          icon 
          small
          color="n2"
          @click="openAll()"
          class="notifications-expand-btn"
          title="View All Notifications"
        >
          <v-icon small>mdi-arrow-expand</v-icon>
        </v-btn>
      </div>
    </v-menu>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Avatar from 'vue-avatar'
import GlobalNotificationCard from '@/components/_Common/GlobalNotificationCard'
import _ from 'lodash'
export default {
  name: 'Notifications',
  components: { Avatar, GlobalNotificationCard },
  props: {
    openAllNotificationsModal: Function,
  },
  data() {
    return {
      showNotificationsBlock: false,
      timer: null,
      defaultScroll: false,
      menu: false,
    }
  },
  computed: {
    ...mapState({
      notifications: (state) => _.cloneDeep(state.notifications.list),
      globalNotifications: (state) => state.notifications.globalNotifications,
      activeTab: (state) => state.notifications.activeTab,
      unreadCounts: (state) => state.notifications.unreadCounts,
    }),
    messages() {
      return this.$store.state.notifications.messages
    },
    activityNotifications() {
      return this.notifications || []
    },
    has_unread_notifications() {
      return (this.unreadCounts.activity || 0) > 0 || (this.unreadCounts.global || 0) > 0
    },
    hasUnreadNotifications() {
      const counts = this.unreadCounts || { activity: 0, global: 0 }
      if (this.activeTab === 'activity') {
        return (counts.activity || 0) > 0
      } else if (this.activeTab === 'global') {
        return (counts.global || 0) > 0
      }
      return false
    },
  },
  methods: {
    showNotifications() {
      if (this.timer != null) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.showNotificationsBlock = true
      this.timer = setTimeout(this.hideNotifications, 3000)
    },
    hideNotifications() {
      this.$store.commit('notifications/removeAllMessages')
      this.showNotificationsBlock = false
    },
    avatarEmpty(notification) {
      return (
        notification.createdAvatarUrl == null ||
        notification.createdAvatarUrl.length == 0
      )
    },
    initials(notification) {
      return notification.createdByName[0].toUpperCase()
    },
    changeViewedState(notification) {
      this.$store.dispatch('notifications/updateViewState', notification.id)
    },
    setAllToView() {
      this.$store.dispatch('notifications/setAllToView')
    },
    setActiveTab(tab) {
      this.$store.dispatch('notifications/setActiveTab', tab)
    },
    markAllAsRead() {
      if (this.activeTab === 'activity') {
        this.$store.dispatch('notifications/setAllToView')
      } else if (this.activeTab === 'global') {
        this.$store.dispatch('notifications/markAllGlobalNotificationsAsRead')
      }
    },
    markGlobalNotificationAsRead(notificationId) {
      this.$store.dispatch('notifications/markGlobalNotificationAsRead', notificationId)
    },
    moveToObject(notification) {
      if (
        [
          'new_version',
          'version_deletion',
          'version_rollback',
          'site_in_project',
        ].includes(notification.notificationType)
      ) {
        const pathToObject = `/site/${notification.linkInfo['site_id']}/versions`
        if (
          this.$route.path == pathToObject ||
          notification.notificationType == 'site_in_project'
        )
          document.location = pathToObject
        else {
          this.$router.push(pathToObject)
          this.menu = false
        }
      } else if (
        ['comments', 'mentions'].includes(notification.notificationType)
      ) {
        let pathToObject = `/site/${notification.linkInfo['site_id']}/versions?versionId=${notification.linkInfo['version_id']}`
        pathToObject += `&commentId=${notification.linkInfo['comment_id']}`
        document.location = pathToObject
      } else if (notification.notificationType == 'invitation')
        document.location = `/${notification.linkInfo['object_type'].toLowerCase()}/${notification.linkInfo['object_id']}/access`
    },
    openAll() {
      this.menu = false
      this.openAllNotificationsModal()
    },
    goToSettings() {
      this.menu = false
      this.$router.push('/profile/settings')
    },
  },
  async mounted() {
    // Load the combined feed when component mounts
    try {
      await this.$store.dispatch('notifications/loadCombinedFeed')
    } catch (error) {
      console.error('Error loading notifications:', error)
      // Fallback to loading regular notifications
      try {
        await this.$store.dispatch('notifications/load', false)
      } catch (fallbackError) {
        console.error('Fallback loading failed:', fallbackError)
      }
    }
  },
  watch: {
    '$store.state.notifications.messages': {
      handler(newVal, oldVal) {
        this.showNotifications()
      },
      deep: true,
    },
    menu: {
      handler(newVal, oldVal) {
        if (!newVal) this.defaultScroll = false
      },
    },
  },
}
</script>

<style lang="scss" scoped>
$style: 'Notifications';
::v-deep .mention {
  color: $Y1;
}

::v-deep .#{$style}__html_message p {
  margin-bottom: 0px;
}

::v-deep .#{$style}__text p {
  margin-bottom: 0px;
}

.#{$style} {
  &__card {
    max-width: 384px;
    max-height: 432px;
    border-bottom-left-radius: 0px !important;
    border-bottom-right-radius: 0px !important;
    box-shadow: none !important;
  }

  &__avatar_image {
    top: 20px;
    position: absolute;
  }

  &__version_number {
    .version_dot {
      display: inline-block;
      width: 5px;
      height: 5px;
      background: #585b6a;
      vertical-align: middle;
      margin-left: 3px;
      border-radius: 50%;
      margin-right: 3px;
    }
  }

  &__html_message {
    margin-top: 10px;
    border-radius: 6px;
    background: $N7;
    padding: 8px;
    max-height: 50px;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__unread_dot {
    width: 10px;
    height: 10px;
    background: red;
    border-radius: 50%;
    position: absolute;
    right: 15px;
    top: 21px;
  }

  &__text {
    margin-left: 35px;
    margin-right: 20px;
  }

  &__card_header {
    border-bottom: 1px solid $N7;
    background: $N5;
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding-left: 20px;
    padding-right: 16px;
    padding-top: 16px;
    padding-bottom: 16px;

    .settings_icon {
      color: $N2;
      
      &:hover {
        color: $Y1;
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    .header_text {
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;
    }
  }

  &__tabs {
    display: flex;
    background: $N5;
    border-bottom: 1px solid $N7;
  }

  &__tab {
    flex: 1;
    padding: 12px 16px;
    background: transparent;
    border: none;
    color: $N2;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    &:hover {
      color: $white;
      background: rgba(255, 255, 255, 0.05);
    }

    &.active {
      color: $Y1;
      background: rgba(255, 126, 33, 0.1);
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: $Y1;
      }
    }
  }

  &__tab-badge {
    background: $Y1;
    color: $white;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 10px;
    min-width: 16px;
    text-align: center;
    line-height: 1.2;
  }

  &__global-list {
    padding: 16px 20px;
    max-height: 316px;
    overflow-y: auto;
    background: $N5 !important;
  }

  &__empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    color: $N2;

    .v-icon {
      margin-bottom: 12px;
      font-size: 48px !important;
    }

    p {
      margin: 0 0 4px 0;
      font-size: 14px;
      font-weight: 500;
    }

    small {
      font-size: 12px;
      opacity: 0.8;
    }
  }

  &__footer {
    height: 64px;
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 17px;
    padding-bottom: 15px;
    border-radius: 0px 0px 8px 8px;
    border-top: 1px solid $N7;
    background: $N5;

    .v-btn {
      font-size: 12px;
      height: 32px;
    }
  }

  &__list {
    background: $N5 !important;
    max-height: 316px;
    overflow: scroll;
    overscroll-behavior: contain;
    overflow-y: auto;

    .viewed_button {
      display: none;
      &:hover {
        cursor: pointer;
        i {
          color: $Y1 !important;
        }
      }
    }

    .v-list-item {
      padding-left: 20px;
      padding-right: 20px;
      padding-top: 16px;
      padding-bottom: 16px;

      &:hover {
        background: $N7 !important;
        .#{$style}__unread_dot {
          display: none !important;
        }

        .viewed_button {
          display: block;
          position: absolute;
          top: 16px;
          right: 10px;
        }
      }

      &:not(:first-child) {
        border-top: 1px solid $N7;
      }
    }
  }
}
.avatar-placeholder-wrapper {
  height: 26px;
  width: 26px;
  border-radius: 100%;
  border: 1px solid #b3b3b3;
  overflow: hidden;

  .avatar-placeholder {
    height: 24px;
  }
}

.notifications-expand-btn {
  float: right;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
  }
}
</style>
